// guidePresence.ts — pure selector for the guide presence (Task 1A). Given a hook
// + state, returns the clip + caption + rotated quote, or null when nothing should
// show. It honours: paused / "my own way" suppression; per-hook frequency caps;
// least-recently-shown quote rotation (never the same quote twice running); H4
// quote-by-act; quote always at H1–H5, occasional at H6. No side effects — the
// caller commits the impression (which updates rotation + caps) via the store.
import { GUIDE_MESSAGES, type GuideHook, type GuideQuote, type ActId } from '@/data/guideMessages';

export interface GuidePersist {
  /** LRU recency per hook (oldest → newest), used to rotate quotes. */
  recentQuoteIds: Partial<Record<GuideHook, string[]>>;
  /** once → true; light → the dayKey it last showed; others → true. */
  seen: Partial<Record<GuideHook, string | boolean>>;
  /** ACT boundaries already shown (H4). */
  boundariesShown: string[];
}

export interface SelectCtx {
  paused: boolean;
  myOwnWay: boolean;
  persist: GuidePersist;
  todayKey: string;
  /** H4 — which ACT just completed. */
  act?: ActId;
  /** H2 — caller already handled this qualifying return. */
  alreadyHandledThisReturn?: boolean;
  /** H5 — already shown this app session. */
  shownThisSession?: boolean;
}

export interface GuideSelection {
  hook: GuideHook;
  clipId: string;
  caption: string;
  quote?: GuideQuote;
}

/** Least-recently-shown pick that never repeats the immediate-last quote. */
function pickLRU(pool: GuideQuote[], recent: string[]): GuideQuote {
  if (pool.length <= 1) return pool[0];
  const last = recent[recent.length - 1];
  const candidates = pool.filter((q) => q.id !== last);
  const score = (q: GuideQuote) => {
    const i = recent.lastIndexOf(q.id);
    return i === -1 ? -1 : i; // never-shown (-1) wins; else the oldest index
  };
  return [...candidates].sort((a, b) => score(a) - score(b))[0] ?? pool[0];
}

export function selectGuideMessage(hook: GuideHook, ctx: SelectCtx): GuideSelection | null {
  // Suppression — ONLY while the programme is paused. "I'm doing these my own way"
  // (ctx.myOwnWay) deliberately no longer silences the guide tiles: it silences the
  // reflection NUDGE SHEETS only (handled in reflectionEngagement.nudgeFor). Pause
  // remains the single full suppressor of the warm guide moments.
  if (ctx.paused) return null;

  const msg = GUIDE_MESSAGES[hook];

  // Frequency caps.
  switch (msg.freq) {
    case 'once':
      if (ctx.persist.seen[hook]) return null;
      break;
    case 'perReturn':
      if (ctx.alreadyHandledThisReturn) return null;
      break;
    case 'perBoundary':
      if (ctx.act && ctx.persist.boundariesShown.includes(ctx.act)) return null;
      break;
    case 'oncePerSession':
      if (ctx.shownThisSession) return null;
      break;
    case 'light':
      if (ctx.persist.seen[hook] === ctx.todayKey) return null;
      break;
    case 'perTrigger':
      break;
  }

  // Quote pool (by-act for H4, else the flat pool).
  const pool = msg.quoteByAct && ctx.act ? msg.quoteByAct[ctx.act] ?? [] : msg.quotePool ?? [];
  const wantQuote = msg.showQuote === 'always' || (msg.showQuote === 'occasional' && Math.random() < 0.4);
  const quote = wantQuote && pool.length ? pickLRU(pool, ctx.persist.recentQuoteIds[hook] ?? []) : undefined;

  return { hook, clipId: msg.clipId, caption: msg.caption, quote };
}
